export const formatCoordinate = (
  coord: number,
  isLatitude: boolean
): string => {
  const direction = isLatitude
    ? coord >= 0
      ? 'N'
      : 'S'
    : coord >= 0
    ? 'E'
    : 'W';
  return `${Math.abs(coord).toFixed(6)}° ${direction}`;
};

export const formatLatLng = (lat: number, lng: number): string => {
  return `${formatCoordinate(lat, true)}, ${formatCoordinate(lng, false)}`;
};

/**
 * A decimal-degree pair: `lat, lon`, separated by a comma or whitespace, each
 * optionally carrying a degree mark and a hemisphere letter. The hemisphere
 * slots are axis-specific, so `0 E, 51 N` is rejected rather than silently
 * swapped. Accepting the `°`/`N`-suffixed form means what `formatLatLng`
 * renders can be pasted straight back in.
 */
const COORDINATE_PAIR =
  /^\s*([+-]?\d+(?:\.\d+)?)\s*°?\s*([NS])?\s*[, ]\s*([+-]?\d+(?:\.\d+)?)\s*°?\s*([EW])?\s*$/i;

/** Numbers in the input, used to tell a coordinate attempt from an address. */
const NUMBER_RUN = /[+-]?\d+(?:\.\d+)?/g;

/** Characters a decimal-degree pair can be built from — nothing alphabetic
 *  beyond the four hemisphere letters. */
const COORDINATE_CHARS = /^[\s\d+\-.,°nsewNSEW]+$/;

/**
 * Parse a decimal-degree `lat, lon` string, or null when the input is not one.
 *
 * Null covers both "this is an address, not coordinates" and "these are
 * coordinates but off the globe" — see `looksLikeCoordinates` to tell the two
 * apart. Out-of-range values are rejected rather than clamped: a typo'd
 * latitude is not a location the operator meant, and silently moving the map
 * somewhere plausible is worse than saying no.
 */
export const parseCoordinates = (
  input: string
): { lat: number; lng: number } | null => {
  const match = COORDINATE_PAIR.exec(input);
  if (!match) {
    return null;
  }

  const [, latText, latHemisphere, lngText, lngHemisphere] = match;
  const lat = Number(latText) * (latHemisphere?.toUpperCase() === 'S' ? -1 : 1);
  const lng = Number(lngText) * (lngHemisphere?.toUpperCase() === 'W' ? -1 : 1);

  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }
  return { lat, lng };
};

/**
 * True when the input reads as an attempt at coordinates — exactly two numbers
 * and no other letters. Lets a caller say "those coordinates are off the
 * globe" instead of sending `91, 200` to a geocoder that will only shrug.
 * Postcodes stay on the address path: `N7 8QN` has letters outside the
 * hemisphere set, and `SW1A` has no second number.
 */
export const looksLikeCoordinates = (input: string): boolean =>
  COORDINATE_CHARS.test(input.trim()) &&
  (input.match(NUMBER_RUN) ?? []).length === 2;

export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Initial great-circle bearing from point 1 to point 2, in degrees clockwise
 * from true north (0–360). Used for per-segment heading labels in the planner.
 */
export const calculateBearing = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δλ = toRadians(lon2 - lon1);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x =
    Math.cos(φ1) * Math.sin(φ2) -
    Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return (θ * (180 / Math.PI) + 360) % 360;
};

/** Mean Earth radius in metres (spherical model). */
const EARTH_RADIUS_M = 6371000;

/**
 * Destination point reached by travelling `distanceM` metres from (lat, lng)
 * along an initial `bearingDeg` (clockwise from north). Spherical forward
 * geodesic — accurate to well under a metre at the sub-km scales the planner
 * draws shapes at. Used to derive shape vertices from centre + radius.
 */
export const destinationPoint = (
  lat: number,
  lng: number,
  distanceM: number,
  bearingDeg: number
): { lat: number; lng: number } => {
  const δ = distanceM / EARTH_RADIUS_M;
  const θ = toRadians(bearingDeg);
  const φ1 = toRadians(lat);
  const λ1 = toRadians(lng);

  const sinφ2 =
    Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ);
  const φ2 = Math.asin(sinφ2);
  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(θ) * Math.sin(δ) * Math.cos(φ1),
      Math.cos(δ) - Math.sin(φ1) * sinφ2
    );

  return { lat: (φ2 * 180) / Math.PI, lng: (λ2 * 180) / Math.PI };
};

const toRadians = (degrees: number): number => {
  return degrees * (Math.PI / 180);
};

export const getBounds = (
  coordinates: Array<[number, number]>
): {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
} | null => {
  if (!coordinates || coordinates.length === 0) {
    return null;
  }

  let minLat = coordinates[0][0];
  let maxLat = coordinates[0][0];
  let minLng = coordinates[0][1];
  let maxLng = coordinates[0][1];

  coordinates.forEach(([lat, lng]) => {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  });

  return { minLat, maxLat, minLng, maxLng };
};
